import { BsSortAlphaDown, BsSortAlphaDownAlt } from "react-icons/bs";
import "../commonStyle.css";
import {
  CFormSelect,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_QUERIES_LIST_DATA } from "../../store/actionTypes";
import CustomPagination from "../customPagination";
import { FiFilter } from "react-icons/fi";
import ProperSpinner from "../spinner/ProperSpinner";
import { getDateAndTimeFormatted } from "../../utils/commonFunctions";
import { useNavigate } from "react-router-dom";
import { FeedbackStatus } from "../../utils/contentTypes";
import ModalV2 from "../customModal/ModalV2";

/**
 * @function QueriesTable
 * @description for QueriesTable
 * @returns JSX for QueriesTable
 */

const QueriesTable = () => {
  const navigate = useNavigate();
  const {
    queriesList,
    pageNo,
    totalPage,
    limit,
    isLoading,
    sortOrder,
    sortBy,
    status,
  } = useSelector((state) => state.QueriesReducer);

  const dispatch = useDispatch();
  const [filterModal, setFilterModal] = useState(false);
  const [localStatus, setLocalStatus] = useState("");

  const handlerSort = (field) => {
    dispatch({
      type: UPDATE_QUERIES_LIST_DATA,
      payload: {
        sortBy: field,
        sortOrder: sortBy === field ? (sortOrder === 1 ? -1 : 1) : 1,
        pageNo: 1,
      },
    });
  };

  const renderSortIcon = (field) => (
    <span onClick={() => handlerSort(field)} className="cursor-pointer">
      {sortBy === field && sortOrder === -1 ? (
        <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
      ) : (
        <BsSortAlphaDown className="sort-icons cursor-pointer" />
      )}
    </span>
  );

  const getSortHeaderClass = (field) =>
    sortBy === field ? "sort-highlight" : "";

  const updatePage = (page) => {
    dispatch({
      type: UPDATE_QUERIES_LIST_DATA,
      payload: { pageNo: page },
    });
  };

  const pageLimitHandler = (e) => {
    const currLimit = e?.target?.value;
    dispatch({
      type: UPDATE_QUERIES_LIST_DATA,
      payload: { limit: currLimit, pageNo: 1 },
    });
  };

  const handleApplyFilter = () => {
    // Clearing applied filter is only allowed via Reset
    if (!localStatus || localStatus === "") {
      setLocalStatus(status);
      setFilterModal(false);
      dispatch({
        type: UPDATE_QUERIES_LIST_DATA,
        payload: { pageNo: 1, status: "" },
      });
      return;
    }

    setFilterModal(false);
    dispatch({
      type: UPDATE_QUERIES_LIST_DATA,
      payload: { pageNo: 1, status: localStatus },
    });
  };

  const handleResetFilter = () => {
    setLocalStatus("");
    setFilterModal(false);
    dispatch({
      type: UPDATE_QUERIES_LIST_DATA,
      payload: { pageNo: 1, status: "" },
    });
  };

  const openFilterModal = () => {
    setLocalStatus(status || "");
    setFilterModal(true);
  };

  const closeFilterModal = () => {
    setLocalStatus(status || "");
    setFilterModal(false);
  };

  const isFilterActive = Boolean(status);

  const renderTableBody = () => {
    return (
      <CTableBody>
        {queriesList?.length > 0 &&
          queriesList?.map((query, i) => {
            const serialNo = (pageNo - 1) * limit + (i + 1);
            return (
              <CTableRow key={i} className="table-data-text">
                <CTableDataCell className="table-heading-10-cell">
                  {serialNo}
                </CTableDataCell>
                <CTableDataCell
                  className="name-link cursor-pointer table-heading-20-cell"
                  onClick={() =>
                    navigate(`/queries/${query._id}`, {
                      state: { queryData: query },
                    })
                  }
                >
                  {query?.name || "N/A"}
                </CTableDataCell>
                <CTableDataCell className="table-heading-20-cell">
                  {query?.email || "N/A"}
                </CTableDataCell>
                <CTableDataCell className="table-heading-10-cell">
                  {query?.status === 4 ? (
                    <span className="badge rounded-pill bg-success">
                      Completed
                    </span>
                  ) : (
                    <span className="badge rounded-pill bg-danger">
                      Pending
                    </span>
                  )}
                </CTableDataCell>
                <CTableDataCell
                  className="text-truncate table-heading-30-cell"
                  style={{ maxWidth: "300px" }}
                >
                  {query?.description || "N/A"}
                </CTableDataCell>
                <CTableDataCell className="text-nowrap table-heading-20-cell">
                  {getDateAndTimeFormatted(query?.createdAt)}
                </CTableDataCell>
              </CTableRow>
            );
          })}
      </CTableBody>
    );
  };

  const renderPagination = () => {
    return (
      <CustomPagination
        limit={limit}
        limitHanlder={pageLimitHandler}
        pageNo={pageNo}
        pageChangeHanlder={updatePage}
        totalPage={totalPage}
      />
    );
  };

  const renderNoDataFound = () => {
    if (isLoading) {
      return (
        <div className="ndf-container">
          <h3 className="ndf-heading">
            <span>{" Fetching queries "}</span> {"..."}
          </h3>
        </div>
      );
    }
    return (
      <div className="ndf-container">
        <h3 className="ndf-heading">
          {"No"}
          <span>{" Queries "}</span> {"available !!!"}
        </h3>
      </div>
    );
  };

  const renderView = () => {
    return (
      <div>
        <ModalV2
          isOpen={filterModal}
          onClose={closeFilterModal}
          title="Filter Queries"
          size="small"
          cancelButtonLabel="Reset"
          confirmButtonLabel="Apply Filter"
          onConfirm={handleApplyFilter}
          onCancel={handleResetFilter}
          closeOnOverlayClick={false}
          cancelOnLeft
        >
          <div className="form-group-v2 mb-0">
            <label htmlFor="queryStatus">Query Status</label>
            <CFormSelect
              id="queryStatus"
              size="sm"
              value={localStatus}
              onChange={(e) => setLocalStatus(e.target.value)}
            >
              <option value="">Show All</option>
              {Object.entries(FeedbackStatus).map(([key, value]) => (
                <option key={value} value={value}>
                  {key}
                </option>
              ))}
            </CFormSelect>
          </div>
        </ModalV2>

        <div className="table-header-container">
          <h3 className="mb-0 text-primary">{"Queries List"}</h3>
          <div
            className="btn btn-outline-primary btn-sm add-btn add-btn-container"
            onClick={openFilterModal}
          >
            <span className="px-2">
              {"Filter"}
              {isFilterActive && <span className="filter-applied-dot" />}
            </span>
            <span className="add-icon">
              <FiFilter />
            </span>
          </div>
        </div>
        <CTable hover responsive>
          <CTableHead className="text-nowrap border">
            <CTableRow>
              <CTableHeaderCell className="bg-body-tertiary table-heading-10-cell">
                {"S. No."}
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-20-cell ${getSortHeaderClass("name")}`}
              >
                {"Name"}
                {renderSortIcon("name")}
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-20-cell ${getSortHeaderClass("email")}`}
              >
                {"Email"}
                {renderSortIcon("email")}
              </CTableHeaderCell>
              <CTableHeaderCell className="bg-body-tertiary table-heading-10-cell">
                {"Status"}
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-30-cell ${getSortHeaderClass("description")}`}
              >
                {"Queries"}
                {renderSortIcon("description")}
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-20-cell ${getSortHeaderClass("createdAt")}`}
              >
                {"Date"}
                {renderSortIcon("createdAt")}
              </CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          {isLoading ? <ProperSpinner /> : renderTableBody()}
        </CTable>
        {queriesList?.length ? renderPagination() : null}
        {(isLoading || queriesList?.length == 0) && renderNoDataFound()}
      </div>
    );
  };

  return <div>{renderView()}</div>;
};

export default QueriesTable;
